import React, { useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Tabs,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import { usePageTitle } from "hooks/usePageTitle";
import { NAVBAR_HEIGHT } from "constants/layout";
import CustomTab from "components/CustomTab";
import { a11yProps, CustomTabPanel } from "components/CustomTabPanel";
import LogsViewerContent from "scenes/logs/LogsViewerContent";
import ServerOverview from "scenes/server/ServerOverview";
import ServerHistory from "scenes/server/ServerHistory";
import ServerDatabase from "scenes/server/ServerDatabase";
import ServerSetup from "scenes/server/ServerSetup";
import ServerDataProvider, {
  useServerData,
} from "scenes/server/ServerDataProvider";
import { CARD_RADIUS, relativeTime, severityToken } from "scenes/server/serverUi";

/**
 * Server page (2026-10-21).
 *
 * Replaces the old "Logs" sidebar entry and keeps the log viewer as its own
 * tab. Every other tab reads snapshots written by the system-cron collector —
 * the web process never shells out for metrics.
 *
 * Tabs:
 *   Stats    — current CPU/RAM/disk/services/SSL/backups
 *   History  — 1h / 6h / 24h / 7d charts
 *   Database — table row counts, sizes, pool, collations
 *   Logs     — the original log viewer (unchanged)
 *   Setup    — is the collector running, and what is monitored
 *
 * LAYOUT (2026-10-21, Amine):
 * Health banner + the refresh action used to live inside the Stats tab, which
 * meant you could not force a collection while looking at History or Logs and
 * the other tabs showed no health at all. Both moved into the page header, so
 * they are present on every tab. The tab bar was the sharp 3px-underline
 * variant with the History range filter sitting directly underneath it, so the
 * filter read as part of the tabs. It is now the rounded pill pattern already
 * used by the Linguist Dashboard, in its own rounded container, with real
 * spacing before the panel content.
 *
 * Admin only — the route guard lives in App.js.
 */

const HEALTH_BANNER = (theme, healthStatus, counts) => {
  if (healthStatus === "critical") {
    const { color, Icon } = severityToken(theme, "critical");
    return {
      color,
      Icon,
      title: `${counts.critical} critical issue${counts.critical === 1 ? "" : "s"} need attention`,
    };
  }
  if (healthStatus === "warning") {
    const { color, Icon } = severityToken(theme, "warning");
    return {
      color,
      Icon,
      title: `${counts.warning} warning${counts.warning === 1 ? "" : "s"} to review`,
    };
  }
  if (healthStatus === "ok") {
    const { color, Icon } = severityToken(theme, "ok");
    return { color, Icon, title: "All systems nominal" };
  }
  const { color, Icon } = severityToken(theme, "unknown");
  return { color, Icon, title: "No data collected yet" };
};

const ServerHeader = () => {
  const theme = useTheme();
  const isMobile = !useMediaQuery("(min-width:600px)");
  const {
    healthStatus,
    counts,
    snapshot,
    staleness,
    issues,
    refreshing,
    refresh,
  } = useServerData();

  const banner = HEALTH_BANNER(theme, healthStatus, counts);

  return (
    <Box
      sx={{
        flexShrink: 0,
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        flexWrap: "wrap",
        p: isMobile ? 1.5 : 2,
        mb: 1.5,
        borderRadius: CARD_RADIUS,
        border: `1px solid ${banner.color}55`,
        bgcolor: `${banner.color}0d`,
      }}
    >
      <Box sx={{ color: banner.color, display: "flex", flexShrink: 0 }}>
        <banner.Icon />
      </Box>

      <Box sx={{ flex: 1, minWidth: 180 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 700, color: banner.color, lineHeight: 1.3 }}
          >
            {banner.title}
          </Typography>

          {staleness.isStale && (
            <Chip
              size="small"
              label="Collector stale"
              sx={{
                height: 20,
                borderRadius: "6px",
                fontSize: "0.68rem",
                fontWeight: 700,
                bgcolor: `${theme.palette.warning.main}1a`,
                color: theme.palette.warning.main,
              }}
            />
          )}

          {issues.length > 0 && healthStatus === "ok" && (
            <Chip
              size="small"
              label={`${issues.length} note(s)`}
              sx={{ height: 20, borderRadius: "6px", fontSize: "0.68rem" }}
            />
          )}
        </Box>

        <Typography variant="caption" color="text.secondary" component="div">
          Last collected {relativeTime(snapshot?.CapturedAt)}
          {snapshot?.Hostname ? ` · ${snapshot.Hostname}` : ""}
          {snapshot?.CollectedBy ? ` · via ${snapshot.CollectedBy}` : ""}
          {staleness.isStale ? " · the cron job may not be running" : ""}
        </Typography>
      </Box>

      <Button
        size="small"
        variant="outlined"
        startIcon={
          refreshing ? <CircularProgress size={14} color="inherit" /> : <RefreshRoundedIcon />
        }
        onClick={refresh}
        disabled={refreshing}
        sx={{
          borderRadius: "10px",
          textTransform: "none",
          fontWeight: 600,
          flexShrink: 0,
          borderColor: `${banner.color}66`,
          color: banner.color,
          "&:hover": {
            borderColor: banner.color,
            bgcolor: `${banner.color}14`,
          },
        }}
      >
        {refreshing ? "Collecting…" : "Refresh now"}
      </Button>
    </Box>
  );
};

const ServerScreen = () => {
  const theme = useTheme();
  const isMobile = !useMediaQuery("(min-width:600px)");
  const [tabValue, setTabValue] = useState(0);

  const handleTabsChange = (_event, newValue) => {
    setTabValue(newValue);
  };

  return (
    <Box
      p="1rem 1rem 0 1rem"
      bgcolor={theme.palette.background.alt}
      sx={{
        flex: 1,
        position: "relative",
        height: `calc(100vh - ${NAVBAR_HEIGHT + (isMobile ? 0 : 20)}px)`,
        "@supports (height: 100dvh)": {
          height: `calc(100dvh - ${NAVBAR_HEIGHT + (isMobile ? 0 : 20)}px)`,
        },
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
      }}
    >
      <ServerHeader />

      {/* Rounded pill tabs — the same pattern as the Linguist Dashboard.
          The default 3px underline indicator is a hard-edged rectangle that
          read as a divider rather than a selection, and it sat flush against
          the panel content. */}
      <Box
        sx={{
          flexShrink: 0,
          mb: 2,
          p: "3px",
          borderRadius: CARD_RADIUS,
          border: `1px solid ${theme.palette.divider}`,
          bgcolor: theme.palette.background.paper,
          minWidth: 0,
        }}
      >
        <Tabs
          value={tabValue}
          onChange={handleTabsChange}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{
            minHeight: 40,
            "& .MuiTabs-indicator": { display: "none" },
            "& .MuiTabs-scrollButtons": {
              "&.Mui-disabled": { opacity: 0.3 },
            },
            "& .MuiTab-root": {
              minHeight: 36,
              minWidth: 0,
              px: 2,
              py: 0.5,
              mx: "2px",
              borderRadius: "9px",
              fontSize: "0.875rem",
              fontWeight: 500,
              textTransform: "none",
              color: theme.palette.text.secondary,
              transition: "background-color 0.18s ease, color 0.18s ease",
              "&:hover": {
                bgcolor: theme.palette.action.hover,
                color: theme.palette.text.primary,
              },
              "&.Mui-selected": {
                bgcolor: theme.palette.primary.main,
                color: theme.palette.background.default,
                fontWeight: 700,
                "&:hover": {
                  bgcolor: theme.palette.primary.main,
                  color: theme.palette.background.default,
                },
              },
            },
          }}
        >
          <CustomTab label="Stats" {...a11yProps(0)} />
          <CustomTab label="History" {...a11yProps(1)} />
          <CustomTab label="Database" {...a11yProps(2)} />
          <CustomTab label="Logs" {...a11yProps(3)} />
          <CustomTab label="Setup" {...a11yProps(4)} />
        </Tabs>
      </Box>

      <Box sx={{ flex: 1, minHeight: 0, overflow: "auto", pb: 1 }}>
        <CustomTabPanel value={tabValue} index={0} sx={{ p: 0, pt: 0 }}>
          <ServerOverview />
        </CustomTabPanel>

        <CustomTabPanel value={tabValue} index={1} sx={{ p: 0, pt: 0 }}>
          <ServerHistory />
        </CustomTabPanel>

        <CustomTabPanel value={tabValue} index={2} sx={{ p: 0, pt: 0 }}>
          <ServerDatabase />
        </CustomTabPanel>

        <CustomTabPanel value={tabValue} index={3} sx={{ p: 0, pt: 0 }}>
          <LogsViewerContent />
        </CustomTabPanel>

        <CustomTabPanel value={tabValue} index={4} sx={{ p: 0, pt: 0 }}>
          <ServerSetup />
        </CustomTabPanel>
      </Box>
    </Box>
  );
};

const Server = () => {
  usePageTitle("Server");

  return (
    <ServerDataProvider>
      <ServerScreen />
    </ServerDataProvider>
  );
};

export default Server;
